import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  DatePicker,
  TimePicker,
  Input,
  Button,
  notification,
  Spin,
  Card,
  Tag,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getServices,
  getServicesCategories,
  getServiceAvailability,
  getAvailableResources,
  createServiceOrder,
  getPaymentMethods,
  getResourcesCategories,
  createPayments,
} from "../../../service";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

interface CreateReservationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  branchId: number | null;
}

export const CreateReservationModal: React.FC<
  CreateReservationModalProps
> = ({ open, setOpen, branchId }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const [selectedServiceCategory, setSelectedServiceCategory] =
    useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [selectedServicePrice, setSelectedServicePrice] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [resourceCategories, setResourceCategories] = useState<any[]>([]);
  const [selectedResources, setSelectedResources] = useState<any>({});

  // Pobierz kategorie usług
  const { data: serviceCategories, isLoading: loadingCategories } = useQuery(
    ["serviceCategories"],
    getServicesCategories,
    { enabled: open }
  );

  // Pobierz usługi dla wybranej kategorii
  const { data: services, isLoading: loadingServices } = useQuery(
    ["services", selectedServiceCategory],
    () =>
      getServices({
        id: selectedServiceCategory!,
      }),
    { enabled: !!selectedServiceCategory && open }
  );

  // Pobierz dostępność usługi
  const { refetch: refetchAvailability, isLoading: loadingAvailability } =
    useQuery(
      ["serviceAvailability", selectedService, selectedDate],
      () =>
        getServiceAvailability({
          serviceId: selectedService!,
          date: selectedDate!.format("YYYY-MM-DD"),
        }),
      {
        enabled: false,
        onSuccess: (data) => {
          setAvailableTimeSlots(data || []);
        },
      }
    );

  // Pobierz kategorie zasobów
  const { data: resourceCategoriesData } = useQuery(
    ["resourceCategories"],
    getResourcesCategories,
    { enabled: open }
  );

  // Pobierz metody płatności
  const { data: paymentMethods } = useQuery(
    ["paymentMethods", branchId],
    () => getPaymentMethods({ branchId: branchId! }),
    { enabled: !!branchId && open }
  );

  // Mutacja do tworzenia płatności
  const { mutateAsync: createPaymentConfirmation, isLoading: creatingPayment } = useMutation(
    createPayments,
    {
      onSuccess: (response) => {
        if (response.status === 201) {
          notification.success({
            message: t("createReservationModal.reservationCreated"),
            placement: "bottomRight",
          });
          queryClient.invalidateQueries(["service-orders-calendar"]);
          queryClient.invalidateQueries(["/service-orders-calendar"]);
          queryClient.invalidateQueries(["/services"]);
          handleCancel();
        }
      },
      onError: (error: any) => {
        notification.error({
          message: t("createReservationModal.paymentCreationError"),
          description:
            error?.response?.data?.message ||
            t("createReservationModal.paymentCreationFailed"),
          placement: "bottomRight",
        });
      },
    }
  );

  // Mutacja do tworzenia rezerwacji
  const { mutateAsync: createReservation, isLoading: creating } = useMutation(
    createServiceOrder,
    {
      onSuccess: (response) => {
        if (response.status === 201) {
          // Po stworzeniu rezerwacji, utwórz płatność
          const selectedPaymentMethod = paymentMethods?.find(
            (pm: any) => pm.id === response.data.details?.paymentMethodId
          );

          createPaymentConfirmation({
            amount: response.data.price,
            content: {},
            paymentMethodId: selectedPaymentMethod?.id,
            serviceOrderId: response.data.id,
            email: response.data.email,
            urlReturn: window.location.origin,
          });
        }
      },
      onError: (error: any) => {
        notification.error({
          message: t("createReservationModal.reservationCreationError"),
          description:
            error?.response?.data?.message ||
            t("createReservationModal.reservationCreationFailed"),
          placement: "bottomRight",
        });
      },
    }
  );

  // Pobierz dostępne zasoby dla danej kategorii
  const fetchAvailableResources = async (
    categoryId: number,
    date: string
  ) => {
    try {
      const resources = await getAvailableResources({
        resourceCategoryId: categoryId,
        serviceId: selectedService!,
        date,
      });
      return resources;
    } catch (error) {
      console.error("Error fetching resources:", error);
      return [];
    }
  };

  useEffect(() => {
    if (selectedService && selectedDate) {
      refetchAvailability();
    }
  }, [selectedService, selectedDate, refetchAvailability]);

  useEffect(() => {
    if (selectedService && selectedDate && resourceCategoriesData) {
      // Pobierz dostępne zasoby dla każdej kategorii
      resourceCategoriesData.forEach(async (category: any) => {
        const resources = await fetchAvailableResources(
          category.id,
          selectedDate.format("YYYY-MM-DD")
        );
        setResourceCategories((prev) => {
          const updated = prev.filter((c) => c.id !== category.id);
          return [...updated, { ...category, resources }];
        });
      });
    }
  }, [selectedService, selectedDate, resourceCategoriesData]);

  // Resetuj stan gdy modal się otwiera
  useEffect(() => {
    if (!open) {
      setSelectedServiceCategory(null);
      setSelectedService(null);
      setSelectedServicePrice(0);
      setSelectedDate(null);
      setAvailableTimeSlots([]);
      setResourceCategories([]);
      setSelectedResources({});
    }
  }, [open]);

  // Automatycznie wybierz metodę płatności gdy jest tylko jedna
  useEffect(() => {
    if (open && paymentMethods && paymentMethods.length === 1) {
      form.setFieldValue("paymentMethodId", paymentMethods[0].id);
    }
  }, [paymentMethods, form, open]);

  // Automatycznie wybierz kategorię usług gdy jest tylko jedna
  useEffect(() => {
    if (open && serviceCategories && serviceCategories.length === 1) {
      setSelectedServiceCategory(serviceCategories[0].id);
      form.setFieldValue("serviceCategoryId", serviceCategories[0].id);
    }
  }, [serviceCategories, form, open]);

  // Automatycznie wybierz usługę gdy jest tylko jedna
  useEffect(() => {
    if (open && services && services.length === 1) {
      setSelectedService(services[0].id);
      setSelectedServicePrice(services[0].price || 0);
      form.setFieldValue("serviceId", services[0].id);
    }
  }, [services, form, open]);

  const handleCancel = () => {
    form.resetFields();
    setSelectedServiceCategory(null);
    setSelectedService(null);
    setSelectedServicePrice(0);
    setSelectedDate(null);
    setAvailableTimeSlots([]);
    setResourceCategories([]);
    setSelectedResources({});
    setOpen(false);
  };

  const handleSubmit = async (values: any) => {
    try {
      const startTime = dayjs.utc(
        `${selectedDate?.format("YYYY-MM-DD")} ${values.time}`,
        "YYYY-MM-DD HH:mm"
      );

      const selectedPaymentMethod = paymentMethods?.find(
        (pm: any) => pm.id === values.paymentMethodId
      );

      const requestedResources: any = {};
      Object.keys(selectedResources).forEach((categoryId) => {
        if (selectedResources[categoryId]) {
          requestedResources[categoryId] = selectedResources[categoryId];
        }
      });

      const selectedServiceData = services?.find(
        (s: any) => s.id === selectedService
      );

      await createReservation({
        price: selectedServicePrice,
        startTime: startTime.toISOString(),
        details: {
          cart: [
            {
              cart_positoin: {
                id: selectedService,
                meal_name: selectedServiceData?.name || "",
                meal_price: selectedServicePrice,
                meal_options: [],
                meal_quantity: 1,
              },
            },
          ],
          payments: selectedPaymentMethod?.name,
          paymentsType: selectedPaymentMethod?.type,
          paymentMethodId: selectedPaymentMethod?.id,
          resources: requestedResources,
          deliveryTime: "FUTURE",
          additionalSections: [],
        },
        email: values.email,
        clientName: values.clientName,
        telNumber: values.telNumber,
        serviceId: selectedService,
        companyBranchId: branchId,
      });
    } catch (error) {
      console.error("Error creating reservation:", error);
    }
  };
  return (
    <Modal
      title={t("createReservationModal.title")}
      open={open}
      onCancel={handleCancel}
      width={800}
      footer={null}
    >
      <Spin spinning={creating || creatingPayment}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t("createReservationModal.serviceCategoryLabel")}
            name="serviceCategoryId"
            rules={[
              {
                required: true,
                message: t("createReservationModal.serviceCategoryRequired"),
              },
            ]}
          >
            <Select
              placeholder={t("createReservationModal.serviceCategoryPlaceholder")}
              loading={loadingCategories}
              onChange={(value) => {
                setSelectedServiceCategory(value);
                setSelectedService(null);
                setSelectedServicePrice(0);
                form.setFieldValue("serviceId", undefined);
              }}
            >
              {serviceCategories?.map((category: any) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("createReservationModal.serviceLabel")}
            name="serviceId"
            rules={[
              {
                required: true,
                message: t("createReservationModal.serviceRequired"),
              },
            ]}
          >
            <Select
              placeholder={t("createReservationModal.servicePlaceholder")}
              loading={loadingServices}
              disabled={!selectedServiceCategory}
              onChange={(value) => {
                setSelectedService(value);
                const service = services?.find((s: any) => s.id === value);
                if (service) {
                  setSelectedServicePrice(service.price || 0);
                }
              }}
            >
              {services?.map((service: any) => (
                <Select.Option key={service.id} value={service.id}>
                  {t("createReservationModal.serviceOption", {
                    name: service.name,
                    price: ((service.price || 0) / 100).toFixed(2),
                    duration: service.duration,
                  })}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("createReservationModal.dateLabel")}
            name="date"
            rules={[
              {
                required: true,
                message: t("createReservationModal.dateRequired"),
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabled={!selectedService}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              onChange={(date) => {
                setSelectedDate(date);
                setAvailableTimeSlots([]);
                form.setFieldValue("time", undefined);
              }}
            />
          </Form.Item>

          {selectedDate && selectedService && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Spin spinning={loadingAvailability}>
                <div style={{ marginBottom: 8 }}>
                  <strong>{t("createReservationModal.availableHours")}</strong>
                </div>
                {availableTimeSlots.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {availableTimeSlots.map((slot) => (
                      <Tag key={slot} color="blue">
                        {slot}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <div>{t("createReservationModal.noAvailableSlots")}</div>
                )}
              </Spin>
            </Card>
          )}

          <Form.Item
            label={t("createReservationModal.hourLabel")}
            name="time"
            rules={[
              {
                required: true,
                message: t("createReservationModal.hourRequired"),
              },
            ]}
          >
            <Select
              placeholder={t("createReservationModal.hourPlaceholder")}
              disabled={!selectedDate || availableTimeSlots.length === 0}
            >
              {availableTimeSlots.map((slot) => (
                <Select.Option key={slot} value={slot}>
                  {slot}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {resourceCategories.map((category) => (
            <Form.Item
              key={category.id}
              label={t("createReservationModal.resourceLabel", {
                name: category.name,
              })}
              name={`resource_${category.id}`}
            >
              <Select
                placeholder={t("createReservationModal.resourcePlaceholder", {
                  name: category.name.toLowerCase(),
                })}
                allowClear
                onChange={(value) => {
                  setSelectedResources((prev: any) => ({
                    ...prev,
                    [category.name]: value,
                  }));
                }}
              >
                {category.resources?.map((resource: any) => (
                  <Select.Option
                    key={resource.id}
                    value={resource.id}
                    disabled={
                      !resource.availability?.includes(
                        form.getFieldValue("time")
                      )
                    }
                  >
                    {resource.name}
                    {!resource.availability?.includes(
                      form.getFieldValue("time")
                    ) && ` ${t("createReservationModal.resourceUnavailable")}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ))}

          <Form.Item
            label={t("createReservationModal.clientNameLabel")}
            name="clientName"
            rules={[
              {
                required: true,
                message: t("createReservationModal.clientNameRequired"),
              },
            ]}
          >
            <Input placeholder="Jan Kowalski" />
          </Form.Item>

          <Form.Item
            label={t("createReservationModal.phoneNumberLabel")}
            name="telNumber"
            rules={[
              {
                required: true,
                message: t("createReservationModal.phoneNumberRequired"),
              },
              {
                pattern: /^[0-9]{9,}$/,
                message: t("createReservationModal.phoneNumberInvalid"),
              },
            ]}
          >
            <Input placeholder="+48123456789" />
          </Form.Item>

          <Form.Item
            label={t("createReservationModal.emailLabel")}
            name="email"
            rules={[
              {
                required: true,
                message: t("createReservationModal.emailRequired"),
              },
              {
                type: "email",
                message: t("createReservationModal.emailInvalid"),
              },
            ]}
          >
            <Input placeholder="email@example.com" />
          </Form.Item>

          <Form.Item
            label={t("createReservationModal.paymentMethodLabel")}
            name="paymentMethodId"
            rules={[
              {
                required: true,
                message: t("createReservationModal.paymentMethodRequired"),
              },
            ]}
          >
            <Select placeholder={t("createReservationModal.paymentMethodPlaceholder")}>
              {paymentMethods?.map((method: any) => (
                <Select.Option key={method.id} value={method.id}>
                  {method.name} ({method.type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button onClick={handleCancel}>
                {t("createReservationModal.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                {t("createReservationModal.submit")}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};
